import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelectedColoc } from '../hooks/useSelectedColoc';
import { roomsService } from '../services/rooms.service';
import { colocService } from '../services/coloc.service';
import { RoomDetail, RoomStatus, BADGE_FERMEE, getRoomStatusLabel, getRoomStatusColor, RoomCreateRequest } from '../types/room';
import { formatCurrency } from '../utils/format';
import '../styles/RoomsPage.css';
import { getErrorMessage } from '../utils/error';
import { AIDE_NUMERO, AIDE_TANTIEMES } from './roomFields';
import BathroomSharing from '../components/BathroomSharing';
import FieldPreview from '../components/FieldPreview';


/**
 * Aperçus des champs de chambre : où chaque valeur atterrit sur le site public.
 * Définis ici et importés par le wizard — les deux écrans demandent les mêmes
 * champs, deux jeux de libellés finiraient par diverger.
 */
export const APERCUS_CHAMBRE = {
  nom: {
    apercu: 'chambre-vignette.png',
    titre: 'Vignette sur la page de la colocation',
    description: "Le nom titre la vignette, sous la photo, avec l'étage, la surface, la salle de bain et le loyer.",
  },
  loyer: {
    apercu: 'chambre-loyer.png',
    titre: 'Bloc « Loyer » de la page chambre',
    description: "Le loyer hors charges s'affiche en grand, les charges juste en dessous avec ce qu'elles incluent.",
  },
  sdb: {
    apercu: 'chambre-entete.png',
    titre: 'Bandeau en tête de la page chambre',
    description: 'Surface, lit, salle de bain et étage y sont résumés en pictogrammes.',
  },
  description: {
    apercu: 'chambre-description.png',
    titre: '« À propos de cette chambre »',
    description: "Le texte qui décrit la chambre : agencement, mobilier, ce qu'elle partage.",
    exemple: "Cette chambre est située au premier étage. Elle est meublée d'un lit en 120 cm, d'une armoire et d'un bureau. Elle partage la salle de bain avec la chambre voisine.",
  },
};

const RoomsPage: React.FC = () => {
  const navigate = useNavigate();
  const selectedColoc = useSelectedColoc();

  // Data
  const [rooms, setRooms] = useState<RoomDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Encadrement des loyers (loi ELAN) : flag porté par la coloc parente.
  // Conditionne l'affichage du champ "complément de loyer" sur les chambres.
  const [isRentControlled, setIsRentControlled] = useState(false);

  // Filter
  const [statusFilter, setStatusFilter] = useState<RoomStatus | 'all'>('all');

  // Expanded detail

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createFloor, setCreateFloor] = useState('');
  const [createSurface, setCreateSurface] = useState('');
  const [createPriceNIC, setCreatePriceNIC] = useState('');
  const [createRentSupplement, setCreateRentSupplement] = useState('');
  const [createCharges, setCreateCharges] = useState('');
  const [createDeposit, setCreateDeposit] = useState('');
  const [createBathroomShared, setCreateBathroomShared] = useState('');
  const [createAgencyExpenses, setCreateAgencyExpenses] = useState('');
  const [createRoomNumber, setCreateRoomNumber] = useState('');
  const [createTantiemes, setCreateTantiemes] = useState('');
  const [createIntroTxt, setCreateIntroTxt] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Edit modal
  // Delete modal
  const loadData = useCallback(async () => {
    if (!selectedColoc) {
      setRooms([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [data, details] = await Promise.all([
        roomsService.listForColoc(selectedColoc.id),
        colocService.getColocDetails(selectedColoc.id).catch(() => null),
      ]);
      setRooms(data);
      setIsRentControlled(details?.isRentControlled ?? false);
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors du chargement'));
    } finally {
      setLoading(false);
    }
  }, [selectedColoc]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Échap ferme la création. L'édition et la suppression ont suivi le dossier
  // vers `RoomDetailPage`, qui porte son propre écouteur.
  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showCreateModal) setShowCreateModal(false);
    };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [showCreateModal]);

  // Filtered rooms

  /** Le détail d'une chambre, sorti du tableau vers le panneau latéral. */
  const filteredRooms = rooms.filter(r => {
    if (statusFilter === 'all') return true;
    return r.status === statusFilter;
  });

  // --- Handlers ---

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedColoc) return;
    setCreateLoading(true);
    setCreateError(null);
    try {
      const data: RoomCreateRequest = {
        name: createName,
        floor: parseInt(createFloor),
        surface: parseFloat(createSurface),
        priceNIC: parseFloat(createPriceNIC),
        rentSupplement: parseFloat(createRentSupplement) || 0,
        charges: parseFloat(createCharges),
        depositAmount: parseFloat(createDeposit),
        bathroomSharedWith: parseInt(createBathroomShared),
        agencyExpenses: parseFloat(createAgencyExpenses),
        roomNumber: createRoomNumber ? parseInt(createRoomNumber) : null,
        tantiemes: parseInt(createTantiemes),
        introTxt: createIntroTxt,
      };
      await roomsService.create(selectedColoc.id, data);
      setShowCreateModal(false);
      setCreateName('');
      setCreateFloor('');
      setCreateSurface('');
      setCreatePriceNIC('');
      setCreateRentSupplement('');
      setCreateCharges('');
      setCreateDeposit('');
      setCreateBathroomShared('');
      setCreateAgencyExpenses('');
      setCreateRoomNumber('');
      setCreateTantiemes('');
      setCreateIntroTxt('');
      await loadData();
    } catch (err) {
      setCreateError(getErrorMessage(err, 'Erreur lors de la création'));
    } finally {
      setCreateLoading(false);
    }
  };

  if (loading) {
    return <div className="rooms-loading">Chargement...</div>;
  }

  if (!selectedColoc) {
    return <div className="rooms-empty">Sélectionnez une colocation</div>;
  }

  return (
    <div className="rooms-page">
      <div className="rooms-header">
        <h2>Chambres</h2>
        <div className="rooms-header-actions">
          <select
            className="rooms-filter"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as RoomStatus | 'all')}
          >
            <option value="all">Tous les statuts</option>
            <option value="occupied">Occupée</option>
            <option value="reserved">Réservée</option>
            <option value="notice">En préavis</option>
            <option value="available">Libre</option>
          </select>
          <button className="rooms-add-btn" onClick={() => {
            setCreateError(null);
            setShowCreateModal(true);
          }}>
            Ajouter une chambre
          </button>
        </div>
      </div>

      {error && <div className="rooms-error">{error}</div>}

      <div className="rooms-table-container">
        <table className="rooms-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Étage</th>
              <th>Surface</th>
              <th>Loyer HC</th>
              <th>Charges</th>
              <th>Statut</th>
              <th>Occupant</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRooms.map(room => {

              return (
                <React.Fragment key={room.id}>
                  <tr
                    className="rooms-row"
                    onClick={() => navigate(`/rooms/${room.id}`)}
                  >
                    <td className="room-name">{room.name || '-'}</td>
                    <td>{room.floor}</td>
                    <td>{room.surface} m²</td>
                    <td>{formatCurrency(room.priceNIC)}</td>
                    <td>{room.charges != null ? formatCurrency(room.charges) : '-'}</td>
                    <td>
                      <span
                        className="room-status-badge"
                        style={{ backgroundColor: getRoomStatusColor(room.status) }}
                      >
                        {getRoomStatusLabel(room.status)}
                      </span>
                      {room.isClosed && (
                        <span
                          className="room-status-badge"
                          style={{ backgroundColor: BADGE_FERMEE.couleur, marginLeft: 6 }}
                        >
                          {BADGE_FERMEE.libelle}
                        </span>
                      )}
                    </td>
                    <td>{room.tenantName || <span className="no-tenant">—</span>}</td>
                    <td className="rooms-actions" onClick={e => e.stopPropagation()}>
                      <button
                        className="btn-action btn-secondary"
                        onClick={() => navigate(`/rooms/${room.id}`)}
                      >
                        Ouvrir
                      </button>
                    </td>
                  </tr>

                </React.Fragment>
              );
            })}
            {filteredRooms.length === 0 && (
              <tr>
                <td colSpan={8} className="rooms-empty-row">Aucune chambre</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      {/* --- MODALS --- */}

      {/* Create room modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Nouvelle chambre</h3>
            {createError && <div className="rooms-error">{createError}</div>}
            <form onSubmit={handleCreate} className="rooms-form">
              <div className="form-group">
                <label>Nom *<FieldPreview {...APERCUS_CHAMBRE.nom} /></label>
                <input type="text" className="form-control" value={createName} onChange={e => setCreateName(e.target.value)} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Étage *</label>
                  <input type="number" className="form-control" value={createFloor} onChange={e => setCreateFloor(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Surface (m²) *<FieldPreview {...APERCUS_CHAMBRE.sdb} /></label>
                  <input type="number" step="0.1" min="1" className="form-control" value={createSurface} onChange={e => setCreateSurface(e.target.value)} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>{isRentControlled ? 'Loyer de base (HC, €) *' : 'Loyer HC (€) *'}<FieldPreview {...APERCUS_CHAMBRE.loyer} /></label>
                  <input type="number" step="0.01" className="form-control" value={createPriceNIC} onChange={e => setCreatePriceNIC(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Charges (€) *</label>
                  <input type="number" step="0.01" className="form-control" value={createCharges} onChange={e => setCreateCharges(e.target.value)} required />
                </div>
              </div>
              {isRentControlled && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Complément de loyer (€)</label>
                    <input type="number" step="0.01" className="form-control" value={createRentSupplement} onChange={e => setCreateRentSupplement(e.target.value)} />
                    <small style={{ color: '#666', display: 'block', marginTop: 4 }}>
                      Zone encadrée : montant pour caractéristiques exceptionnelles, affiché distinctement sur le site public.
                    </small>
                  </div>
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Dépôt de garantie (€) *</label>
                  <input type="number" step="0.01" className="form-control" value={createDeposit} onChange={e => setCreateDeposit(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Frais d'agence (€) *</label>
                  <input type="number" step="0.01" className="form-control" value={createAgencyExpenses} onChange={e => setCreateAgencyExpenses(e.target.value)} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <BathroomSharing id="create-sdb" value={createBathroomShared === '' ? null : Number(createBathroomShared)}
                    onChange={(v) => setCreateBathroomShared(String(v))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>N° de chambre (interne)</label>
                  <input type="number" className="form-control" value={createRoomNumber} onChange={e => setCreateRoomNumber(e.target.value)} />
                  <small className="form-hint">{AIDE_NUMERO}</small>
                </div>
                <div className="form-group">
                  <label>Tantièmes *</label>
                  <input type="number" className="form-control" value={createTantiemes} onChange={e => setCreateTantiemes(e.target.value)} required />
                  <small className="form-hint">{AIDE_TANTIEMES}</small>
                </div>
              </div>
              <div className="form-group">
                <label>Description *<FieldPreview {...APERCUS_CHAMBRE.description} /></label>
                <textarea className="form-control" rows={3} value={createIntroTxt} onChange={e => setCreateIntroTxt(e.target.value)} required />
              </div>
              <small style={{ display: "block", marginTop: "8px", color: "#666" }}>
                L'URL publique de la chambre sera disponible après la création.
              </small>
              <div className="modal-actions">
                <button type="button" className="btn-action btn-secondary" onClick={() => setShowCreateModal(false)}>Annuler</button>
                <button type="submit" className="btn-action btn-save" disabled={createLoading || !createName.trim()}>
                  {createLoading ? 'Création...' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default RoomsPage;
